<template>
  <div>
    <load-profile />
    <!-- Table Container Card -->
    <b-card>
      <validation-observer
        ref="createForm"
        #default="{ invalid }"
      >
        <!-- form -->
        <n-form-confirmation
          key="createForm"
          :form="$refs.createForm"
          :disabled="invalid"
          confirm-button-text="button.create"
          @submit="submit"
        >
          <div style="width: 100%; display: flex; gap: 1rem;">
            <!-- Department Name with Language Selection -->
            <validation-provider
              v-slot="{ errors }"
              :name="$t('field.departmentName')"
              :rules="
                locale === $i18n.locale.split('-')[0]
                  ? 'required|max:100'
                  : 'max:100'
              "
              style="width: 100%;"
            >
              <b-form-group
                :label="$t('field.departmentName')"
                :state="errors.length === 0 ? null : false"
              >
                <div class="d-flex align-items-center">
                  <div class="flex-grow-1 mr-1">
                    <b-form-input
                      v-model="data.name[locale]"
                      :placeholder="$t('field.departmentName')"
                      :state="errors.length === 0 ? null : false"
                    />
                  </div>
                  <div style="min-width: 80px">
                    <b-form-select
                      v-model="locale"
                      :options="locales"
                      class="language-select"
                    />
                  </div>
                </div>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              :name="$t('breadcrumb.workingSchedule')"
              rules="required"
              style="width: 100%;"
            >
              <b-form-group
                :label="$t('breadcrumb.workingSchedule')"
                :state="errors.length === 0 ? null : false"
                class="mb-2"
              >
                <n-async-single-select
                  v-model="data.workScheduleId"
                  repository="workingScheduleTemplate"
                  selection-key="id"
                  selection-label="translations"
                  :state="errors.length === 0 ? null : false"
                  :placeholder="$t('breadcrumb.workingSchedule')"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </div>
          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <n-button-loading
                v-if="$can('create', permissionType)"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="submit"
                class="mt-1 mr-1"
                variant="primary"
                :loading="loading"
                :disabled="invalid || !data.name[locale]"
              >
                {{ $t('button.create') }}
              </n-button-loading>
              <n-button-loading
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                :loading="loading"
                class="mt-1"
                @submit="back"
              >
                {{ $t('button.cancel') }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BCard,
  BCardText,
  BCardHeader,
  BCardTitle,
  BLink,
  BFormRadioGroup,
  BFormCheckbox,
  BFormSelect,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import NButtonLoading from '@/components/NButtonLoading'
import NFormConfirmation from '@/components/NFormConfirmation'
import NInput from '@/components/NInput'
import NTable from '@/components/NTable'
import { mapValidationMessage } from '@/libs/helper'
import NAsyncSingleSelect from '@/components/NAsyncSingleSelect.vue'
import formInput from './formInput'

const DepartmentRepository = Repository.get('department')
const ProjectRepository = Repository.get('project')

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BCardText,
    BCardHeader,
    BCardTitle,
    BLink,
    BFormRadioGroup,
    BFormCheckbox,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    NTable,
    BFormSelect,
    NAsyncSingleSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        name: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        workScheduleId: null,
      },
      loading: false,
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
    }
  },
  watch: {
    '$i18n.locale'(val) {
      const [language] = val.split('-')
      this.locale = language
    },
  },
  methods: {
    formatErrorMessage(message) {
      return message ? this.$t(message) : ''
    },
    // Helper method to convert string name to object
    convertNameToObject(name) {
      if (typeof name === 'string') {
        const defaultName = name
        const nameObj = {
          en: defaultName,
          zh: '',
          km: '',
          vi: '',
        }
        // Set the current locale's value
        const currentLocale = this.$i18n.locale.split('-')[0]
        if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
          nameObj[currentLocale] = defaultName
        }
        return nameObj
      }
      return name
    },
    submit() {
      this.$refs.createForm.validate().then(success => {
        if (success) {
          this.save()
        }
      })
    },
    save() {
      this.loading = true

      // Ensure name is an object before submission
      const formData = { ...this.data }
      formData.name = this.convertNameToObject(formData.name)

      DepartmentRepository.create(formData)
        .then(() => {
          this.$router.push({
            name: `list-${this.route}`,
          })
        })
        .catch(error => {
          if (error.response?.status === 422) {
            const errorMessage = mapValidationMessage(
              error.response.data.validate,
            )
            this.$refs.createForm.setErrors(errorMessage)
          }
        })
        .then(() => {
          this.loading = false
        })
    },
    back() {
      this.$router.back()
    },
  },
  async beforeRouteEnter(to, from, next) {
    if (!to.params.projectId) {
      return next()
    }

    try {
      const response = await ProjectRepository.show(to.params.projectId)
      const data = response?.data?.data
      if (!data || !data.id) {
        // If no data or invalid data, redirect back to department list
        return next({ name: 'list-project-department' })
      }

      const getProjectName = data => {
        const locale = localStorage.getItem('locale') || 'en'
        return data.name?.[locale] || data.name?.zh || data.name?.en || data.name?.km || data.name?.vi || ''
      }

      // Set breadcrumb if data exists
      to.meta.breadcrumb = [
        // {
        //   text: 'breadcrumb.listProject',
        //   to: { name: 'list-project' }
        // },
        {
          text: getProjectName(data),
          to: { name: 'list-project' },
        },
        {
          text: 'breadcrumb.list_department',
          to: { name: 'list-project-department' },
        },
        {
          text: 'breadcrumb.create_department',
          active: true,
        },
      ]

      next()
    } catch (error) {
      // Handle any errors by redirecting to department list
      console.error('Failed to fetch department:', error)
      next({ name: 'list-project' })
    }
  },
  setup() {
    const fields = formInput
    const permissionType = 'manage-users'
    const route = 'department'
    return { fields, permissionType, route }
  },
}
</script>

<style lang="scss" scoped>
.language-select {
  height: 100%;
}
</style>
